import React, { useMemo, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import {
  StyledDiv,
  Typography,
  Spacing,
  Button,
  LoadingScreen,
  AlertDialog,
  CoreLessonPlanCard,
  ICoreLessonPlan,
  Select,
  SelectOption,
  Icon,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

const ALL_FILTER_VALUE = 'All';

export interface ViewCoreLessonPlansProps {
  lessonPlans: ICoreLessonPlan[];
  isLoading?: boolean;
  showErrorPopup?: boolean;
  setShowErrorPopup: (value: boolean) => void;
  onLessonPlanSelected: (lessonPlan: ICoreLessonPlan) => void;
  onCreateNew: (filters: { grade?: string; subject?: string }) => void;
  getSubjectDisplayName?: (subject: string) => string;
  getSubjectOptions?: (grade?: string) => SelectOption[];
  normalizeSubjectForComparison?: (subject: string) => string;
  hasPendingPlans?: boolean;
  pendingPlansCount?: number;
  isPollingActive?: boolean;
  onRefresh?: () => void;
  isOffline?: boolean;
  cacheStats?: {
    total: number;
    ready: number;
    pending: number;
  };
  initialGrade?: string;
  initialSubject?: string;
  hideFilters?: boolean;
  onFiltersChange?: (filters: { grade?: string; subject?: string }) => void;
}

export const ViewCoreLessonPlans: React.FC<ViewCoreLessonPlansProps> = ({
  lessonPlans,
  isLoading = false,
  showErrorPopup = false,
  setShowErrorPopup,
  onLessonPlanSelected,
  onCreateNew,
  getSubjectDisplayName,
  getSubjectOptions,
  normalizeSubjectForComparison,
  hasPendingPlans = false,
  pendingPlansCount = 0,
  isPollingActive = false,
  onRefresh,
  isOffline = false,
  cacheStats,
  initialGrade,
  initialSubject,
  hideFilters = false,
  onFiltersChange,
}) => {
  const [selectedGrade, setSelectedGrade] = useState<string>(
    initialGrade || ALL_FILTER_VALUE,
  );
  const [selectedSubject, setSelectedSubject] = useState<string>(
    initialSubject || ALL_FILTER_VALUE,
  );
  const [showCompletedPlans, setShowCompletedPlans] = useState(false);

  useEffect(() => {
    if (onFiltersChange) {
      const filters = {
        grade: selectedGrade === ALL_FILTER_VALUE ? undefined : selectedGrade,
        subject:
          selectedSubject === ALL_FILTER_VALUE ? undefined : selectedSubject,
      };
      onFiltersChange(filters);
    }
  }, [selectedGrade, selectedSubject, onFiltersChange]);

  const handleCreateNew = () => {
    const filters = {
      grade: selectedGrade === ALL_FILTER_VALUE ? undefined : selectedGrade,
      subject:
        selectedSubject === ALL_FILTER_VALUE ? undefined : selectedSubject,
    };
    onCreateNew(filters);
  };

  useEffect(() => {
    if (
      selectedGrade !== ALL_FILTER_VALUE &&
      selectedSubject !== ALL_FILTER_VALUE &&
      getSubjectOptions
    ) {
      const availableSubjects = getSubjectOptions(selectedGrade);
      const isSubjectAvailable = availableSubjects.some(
        (option) => option.value === selectedSubject,
      );

      if (!isSubjectAvailable) {
        setSelectedSubject(ALL_FILTER_VALUE);
      }
    }
  }, [selectedGrade, selectedSubject, getSubjectOptions]);

  const sortedLessonPlans = useMemo(() => {
    return [...lessonPlans].sort((a, b) => {
      return Number(b.id) - Number(a.id);
    });
  }, [lessonPlans]);

  const filteredLessonPlans = useMemo(() => {
    return sortedLessonPlans.filter((plan) => {
      const targetGrade =
        hideFilters && initialGrade ? initialGrade : selectedGrade;
      const targetSubject =
        hideFilters && initialSubject ? initialSubject : selectedSubject;

      const matchesGrade =
        targetGrade === ALL_FILTER_VALUE || plan.grade === targetGrade;

      const matchesSubject =
        targetSubject === ALL_FILTER_VALUE ||
        (normalizeSubjectForComparison
          ? normalizeSubjectForComparison(plan.subject) ===
            normalizeSubjectForComparison(targetSubject)
          : plan.subject === targetSubject);

      return matchesGrade && matchesSubject;
    });
  }, [
    sortedLessonPlans,
    selectedGrade,
    selectedSubject,
    hideFilters,
    initialGrade,
    initialSubject,
    normalizeSubjectForComparison,
  ]);

  const { activePlans, completedPlans } = useMemo(() => {
    const active = filteredLessonPlans.filter((plan) => !plan.completedAt);
    const completed = filteredLessonPlans.filter((plan) => !!plan.completedAt);
    return { activePlans: active, completedPlans: completed };
  }, [filteredLessonPlans]);

  const gradeOptions: SelectOption[] = [
    { content: 'All Grades', value: ALL_FILTER_VALUE },
    { content: 'Grade 1', value: '1' },
    { content: 'Grade 2', value: '2' },
    { content: 'Grade 3', value: '3' },
    { content: 'Grade 4', value: '4' },
    { content: 'Grade 5', value: '5' },
  ];

  const subjectOptions: SelectOption[] = useMemo(() => {
    const options: SelectOption[] = [
      { content: 'All Subjects', value: ALL_FILTER_VALUE },
    ];

    if (getSubjectOptions && selectedGrade !== ALL_FILTER_VALUE) {
      const gradeSpecificSubjects = getSubjectOptions(selectedGrade);
      return [options[0], ...gradeSpecificSubjects];
    }

    const allSubjects = ['Eng', 'Urdu', 'Math', 'Science'];

    allSubjects.forEach((subject) => {
      const displayName = getSubjectDisplayName
        ? getSubjectDisplayName(subject)
        : subject;
      options.push({ content: displayName, value: subject });
    });

    return options;
  }, [getSubjectDisplayName, getSubjectOptions, selectedGrade]);

  const getEmptyStateMessage = () => {
    const grade = hideFilters && initialGrade ? initialGrade : selectedGrade;
    const subject =
      hideFilters && initialSubject ? initialSubject : selectedSubject;

    if (grade !== ALL_FILTER_VALUE && subject !== ALL_FILTER_VALUE) {
      const subjectName = getSubjectDisplayName
        ? getSubjectDisplayName(subject)
        : subject;
      return `No lesson plans found for Grade ${grade} - ${subjectName}`;
    }

    if (grade !== ALL_FILTER_VALUE) {
      return `No lesson plans found for Grade ${grade}`;
    }

    const subjectName = getSubjectDisplayName
      ? getSubjectDisplayName(subject)
      : subject;
    return `No lesson plans found for ${subjectName}`;
  };

  if (isLoading) {
    return (
      <LoadingScreen
        heading="Loading Lesson Plans"
        message="Please wait while we fetch your lesson plans..."
      />
    );
  }

  return (
    <Container>
      <AlertDialog
        title="Error Loading Lesson Plans"
        description="Failed to load lesson plans. Please try again."
        showPopup={showErrorPopup}
        onClosePopup={() => {
          setShowErrorPopup(false);
        }}
      />

      <Spacing marginTop="xl" marginBottom="lg">
        <StyledDiv flexDirection="column" gap={24}>
          <Typography variant="textBodyLarge" data-cy="view-manage-lesson-plans-text">
            View and manage your generated lesson plans
          </Typography>

          <StyledDiv flexDirection="column" gap={16}>
            <Typography
              variant="textLabelLarge"
              color="var(--md-sys-color-on-surface-variant)"
            >
              {activePlans.length} Active Lesson Plan
              {activePlans.length === 1 ? '' : 's'}
            </Typography>

            {!hideFilters && (
              <FilterContainer>
                <Select
                  key={`grade-${selectedGrade}`}
                  variant="outlined"
                  label="Grade"
                  options={gradeOptions}
                  defaultValue={selectedGrade}
                  onChange={(option) => setSelectedGrade(option.value)}
                  width="200px"
                  data-cy="grade-filter-select"
                />
                <Select
                  key={`subject-${selectedGrade}-${selectedSubject}`}
                  variant="outlined"
                  label="Subject"
                  options={subjectOptions}
                  defaultValue={selectedSubject}
                  onChange={(option) => setSelectedSubject(option.value)}
                  width="200px"
                  data-cy="subject-filter-select"
                />
              </FilterContainer>
            )}
          </StyledDiv>

          {isOffline && cacheStats && (
            <StyledDiv flexDirection="row" alignItem="center" gap={8}>
              <Typography
                variant="textBodyMedium"
                color="var(--md-sys-color-warning)"
              >
                Offline Mode - Showing {cacheStats.total} saved lesson plan
                {cacheStats.total === 1 ? '' : 's'}
              </Typography>
            </StyledDiv>
          )}
          {hasPendingPlans && !isOffline && (
            <StyledDiv flexDirection="row" alignItem="center" gap={8}>
              <Typography
                variant="textBodyMedium"
                color="var(--md-sys-color-secondary)"
              >
                Generating lesson plan{pendingPlansCount === 1 ? '' : 's'}{' '}
                (typically takes 2-5 minutes)
              </Typography>
              {onRefresh && (
                <Button variant="text" onClick={onRefresh}>
                  Refresh Now
                </Button>
              )}
            </StyledDiv>
          )}
        </StyledDiv>
      </Spacing>

      <Spacing marginBottom="xl">
        {activePlans.length > 0 && (
          <LessonPlanGrid>
            {activePlans.map((lessonPlan) => (
              <CoreLessonPlanCard
                key={lessonPlan.id}
                lessonPlan={lessonPlan}
                onViewLessonPlan={onLessonPlanSelected}
                getSubjectDisplayName={getSubjectDisplayName}
              />
            ))}
          </LessonPlanGrid>
        )}

        {completedPlans.length > 0 && (
          <Spacing marginTop={activePlans.length > 0 ? 'xl' : 'none'}>
            <CompletedSectionHeader
              onClick={() => setShowCompletedPlans(!showCompletedPlans)}
            >
              <StyledDiv flexDirection="row" alignItem="center" gap={8}>
                <Icon
                  name={showCompletedPlans ? 'expand_less' : 'expand_more'}
                  size="normal"
                />
                <Typography variant="textTitleMedium">
                  Completed Lesson Plans ({completedPlans.length})
                </Typography>
              </StyledDiv>
            </CompletedSectionHeader>

            {showCompletedPlans && (
              <Spacing marginTop="md">
                <LessonPlanGrid>
                  {completedPlans.map((lessonPlan) => (
                    <CoreLessonPlanCard
                      key={lessonPlan.id}
                      lessonPlan={lessonPlan}
                      onViewLessonPlan={onLessonPlanSelected}
                      getSubjectDisplayName={getSubjectDisplayName}
                    />
                  ))}
                </LessonPlanGrid>
              </Spacing>
            )}
          </Spacing>
        )}

        {filteredLessonPlans.length === 0 &&
          !hideFilters &&
          selectedGrade === ALL_FILTER_VALUE &&
          selectedSubject === ALL_FILTER_VALUE && (
            <EmptyStateContainer>
              <Typography variant="textHeadlineMedium">
                No Lesson Plans Found
              </Typography>
              <Typography variant="textBodyLarge">
                You haven't created any lesson plans yet. Get started by
                creating your first one.
              </Typography>
              <Button variant="filled" onClick={handleCreateNew}>
                Create Your First Lesson Plan
              </Button>
            </EmptyStateContainer>
          )}

        {filteredLessonPlans.length === 0 &&
          (hideFilters ||
            selectedGrade !== ALL_FILTER_VALUE ||
            selectedSubject !== ALL_FILTER_VALUE) && (
            <EmptyStateContainer>
              <Typography variant="textHeadlineMedium">
                No Lesson Plans Found
              </Typography>
              <Typography variant="textBodyLarge">
                {getEmptyStateMessage()}
              </Typography>
              <Button variant="filled" onClick={handleCreateNew}>
                Create New Lesson Plan
              </Button>
            </EmptyStateContainer>
          )}
      </Spacing>

      <StyledCreateButton>
        <Button variant="filled" onClick={handleCreateNew} data-cy="generate-lesson-plan-button">
          + Generate Lesson Plan
        </Button>
      </StyledCreateButton>
    </Container>
  );
};

const FilterContainer = styled.div`
  display: flex;
  gap: var(--rk-spacing-md);
  flex-wrap: wrap;
  align-items: center;

  ${mq(BreakpointSizes.xs)} {
    flex-direction: column;
    align-items: stretch;
    gap: var(--rk-spacing-lg);

    md-outlined-select {
      width: 100% !important;
    }
  }
`;

const LessonPlanGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--rk-spacing-lg);
  width: 100%;

  ${mq(BreakpointSizes.xs)} {
    grid-template-columns: 1fr;
    gap: var(--rk-spacing-md);
  }
  ${mq(BreakpointSizes.sm)} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding-bottom: var(--rk-spacing-xxxxxxl);
  position: relative;

  ${mq(BreakpointSizes.xs)} {
    padding-bottom: var(--rk-spacing-xxxxxxxl);
  }
`;

const StyledCreateButton = styled.div`
  position: fixed;
  bottom: 25px;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(100% - (var(--rk-spacing-xxxxxxxl) * 5));

  ${mq(BreakpointSizes.xs)} {
    width: calc(100% - var(--rk-spacing-lg));
    bottom: 75px;
    height: 78px;
    align-content: center;
    background-color: var(--md-sys-color-on-primary);
    z-index: 0;
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: var(--rk-spacing-lg);
  min-height: 300px;
  padding: var(--rk-spacing-xl) 0;
`;

const CompletedSectionHeader = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--rk-spacing-md) 0;
  width: 100%;
  color: var(--md-sys-color-on-surface);

  &:hover {
    opacity: 0.8;
  }
`;
